import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { DeleteClientsBulkRequest } from '@/modules/onboarding/clients-setup/types/request/delete-clients-bulk.request';
import { MergeClientsRequest } from '@/modules/onboarding/clients-setup/types/request/merge-clients.request';
import { UpdateClientsBulkRequest } from '@/modules/onboarding/clients-setup/types/request/update-client-bulk.request';
import { UpsertClientsBulkRequest } from '@/modules/onboarding/clients-setup/types/request/upsert-client-bulk.request';
import { CsvImportClientsErrorResponse } from '@/modules/onboarding/clients-setup/types/response/csv-import-clients-error.response';
import { EntityValidationErrorResponse } from '@/modules/onboarding/common/types/entity-validation-error.response';
import { EntityValidationReportResponse } from '@/modules/onboarding/common/types/entity-validation-report.response';
import { GetEntitiesParams } from '@/modules/onboarding/common/types/get-entities.params';
import { axiosInstance } from '@/utils/axios';

import { mapClientFromDTO } from '../mappers/client.mapper';
import { Client } from '../types/clients.types';

export interface DeleteClientsParams {
  clientIds: number[];
}

export async function deleteClients(params: DeleteClientsParams): Promise<void> {
  const endpoint = '/api/clients/bulk';
  const requestData: DeleteClientsBulkRequest = { ids: params.clientIds };

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_CLIENTS_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint, { data: requestData })
  );
}

export async function getClients(params: GetEntitiesParams): Promise<PaginatedResponse<Client>> {
  const endpoint = '/api/clients';

  const response = await withApiErrorHandler<PaginatedResponse<any>>(
    endpoint,
    ApiErrorCode.ONBOARDING_CLIENTS_LOAD_FAILED,
    () => axiosInstance.get(endpoint, { params })
  );

  return {
    ...response,
    items: response.items.map(mapClientFromDTO),
  };
}

export async function upsertClientsBulk(params: UpsertClientsBulkRequest): Promise<void> {
  const endpoint = '/api/clients/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_CLIENTS_UPSERT_FAILED, () =>
    axiosInstance.post(endpoint, params)
  );
}

export async function updateClientsBulk(params: UpdateClientsBulkRequest): Promise<void> {
  const endpoint = '/api/clients/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_CLIENTS_UPDATE_FAILED, () =>
    axiosInstance.patch(endpoint, params)
  );
}

export async function getClientsValidationReport(): Promise<EntityValidationReportResponse> {
  const endpoint = '/api/clients/validation-report';

  return withApiErrorHandler(
    endpoint,
    ApiErrorCode.ONBOARDING_CLIENTS_VALIDATION_REPORT_FAILED,
    () => axiosInstance.get(endpoint)
  );
}

export async function mergeClients(params: MergeClientsRequest): Promise<void> {
  const endpoint = '/api/clients/merge';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_CLIENTS_MERGE_FAILED, () =>
    axiosInstance.post(endpoint, params)
  );
}

export async function submitClients(): Promise<EntityValidationErrorResponse> {
  const endpoint = '/api/clients/submit';

  return withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_CLIENTS_SUBMIT_FAILED, () =>
    axiosInstance.post(endpoint)
  );
}

export async function importClientsFromCsv(file: File): Promise<CsvImportClientsErrorResponse> {
  const endpoint = '/api/clients/import';
  const formData = new FormData();
  // Append the original file directly without recreating it
  // This preserves the file's binary signature for magic number detection
  formData.append('file', file);

  return withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_CLIENTS_IMPORT_FAILED, () =>
    axiosInstance.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  );
}
